
import { useEffect, useState, useRef, useCallback } from 'react';

import { fetchMaintenanceConfig } from '../services/remoteConfigService';
import { logger } from '../utils/logger';

const forceDisableMaintenance = process.env.REACT_APP_FORCE_DISABLE_MAINTENANCE === 'true';
const POLL_INTERVAL = 60000; // Check every minute instead of every 30 seconds

export const useMaintenanceMode = () => {
  const [maintenance, setMaintenance] = useState(false);
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);
  const previousMaintenanceValue = useRef(false);

  // Using useCallback to avoid recreating this function on each render
  const fetchConfig = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    if (forceDisableMaintenance) {
      if (loading) {
        logger.info('🛡️ [MaintenanceMode] Forced maintenance override: always OFF via REACT_APP_FORCE_DISABLE_MAINTENANCE');
        setMaintenance(false);
        setLoading(false);
      }
      return;
    }
    
    try {
      const config = await fetchMaintenanceConfig();
      
      // Only update state if the value actually changed or if we're still loading
      if (isMountedRef.current && (loading || previousMaintenanceValue.current !== config.maintenance_mode)) {
        logger.info('🔄 [MaintenanceMode] Remote config maintenance_mode:', config.maintenance_mode);
        setMaintenance(config.maintenance_mode);
        previousMaintenanceValue.current = config.maintenance_mode;
        if (loading) setLoading(false);
      }
    } catch (error) {
      logger.error('❌ [MaintenanceMode] Error fetching maintenance config:', error);
      if (loading && isMountedRef.current) setLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    isMountedRef.current = true;
    
    // Initial fetch
    fetchConfig();
    
    // Poll at a reasonable interval
    const interval = setInterval(fetchConfig, POLL_INTERVAL);
    
    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchConfig]);

  return { maintenance, loading };
};
